import { Check, Target, Info, Sparkles } from "lucide-react";
import DevelopmentMeter from "@/components/DevelopmentMeter";

// Derive the shared development band inputs from whatever SC plan shape the payload carries. RESOLVED =
// genuine developmental objects worked through (problem-type groups done, or steps done, or completed[]),
// NEVER sentence position. READY = the required pass is complete.
function scMeterValues(progress, complete) {
  const groups = progress.plan_groups || [];
  if (groups.length > 0) {
    return { resolved: groups.filter((g) => g.status === "done").length, budget: groups.length,
             ready: complete || groups.every((g) => g.status === "done") };
  }
  const steps = progress.plan_steps || [];
  if (steps.length > 0) {
    const done = progress.plan_done ?? steps.filter((s) => s.status === "done").length;
    return { resolved: done, budget: steps.length, ready: complete || done >= steps.length };
  }
  const done = progress.completed_count ?? (progress.completed || []).length;
  const budget = progress.budget_total ?? (done + (progress.active ? 1 : 0) + (progress.remaining_estimate ?? 0));
  return { resolved: done, budget, ready: complete };
}

function ScMeter({ progress, complete }) {
  const { resolved, budget, ready } = scMeterValues(progress, complete);
  return (
    <div className="mb-4">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.22em] text-stone-500">Your progress</p>
      <DevelopmentMeter
        resolved={resolved}
        budget={budget}
        ready={ready}
        startLabel="Refining your sentences"
        endLabel="Ready for Proofreading"
      />
    </div>
  );
}

// SALIENT, persistent Sentence Craft progress. Answers the learner's three questions at a glance:
// did I do what I just worked on?  ·  am I making progress?  ·  how much is left?
// Progress = INSTRUCTIONAL OBJECTS worked through (+ evidence of control) — never edit/error counts.
// Approximate language on purpose ("about N to go"), never a raw defect count. When a target is
// dropped/invalidated (moved on, or a diagnosis withdrawn) the backend removes it from the budget, so
// the bar + counts here simply reflect the smaller remaining work — the learner is never re-charged.
export default function SentenceCraftProgress({ progress, complete }) {
  if (!progress) return null;

  // PREFERRED: the stable, learner-facing session plan grouped BY PROBLEM TYPE (from A→B→C→D triage).
  // Answers: what is The Writing Studio teaching · what remains · how much is left · why THIS sentence right now.
  // Progress is shown per problem type ("Run-On Sentences 2 of 5"), never a shifting global step count.
  const planGroups = progress.plan_groups || [];
  if (planGroups.length > 0) {
    const cur = progress.current || null;
    const deferred = progress.deferred || [];
    const totalGroups = planGroups.length;
    const doneGroups = complete ? totalGroups : planGroups.filter((g) => g.status === "done").length;
    return (
      <div
        data-testid="sc-progress"
        className="rounded-md border-2 border-stone-200 border-l-[4px] border-l-[#8C3A2A] bg-white shadow-sm px-5 py-4"
      >
        <ScMeter progress={progress} complete={complete} />
        <div className="flex items-baseline justify-between">
          <span className="font-mono-panel text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8C3A2A]">
            Sentence Craft Plan
          </span>
          <span className="font-mono-panel text-[12px] text-stone-600" data-testid="sc-progress-summary">
            {complete ? (
              <span className="text-emerald-700 font-semibold">All {totalGroups} done</span>
            ) : (
              <>
                <span className="text-stone-900 font-semibold">{doneGroups}</span>
                <span className="text-stone-400"> of {totalGroups} problem {totalGroups === 1 ? "type" : "types"}</span>
              </>
            )}
          </span>
        </div>

        {/* EXPLICIT current focus — ties the plan to the highlighted sentence + coaching message. */}
        {!complete && cur && (
          <div
            className="mt-3 rounded-sm bg-[#8C3A2A]/[0.05] border border-[#8C3A2A]/20 px-3.5 py-2.5"
            data-testid="sc-current-focus"
          >
            <div className="font-mono-panel text-[9.5px] uppercase tracking-[0.18em] text-[#8C3A2A]/80">
              Currently working on
            </div>
            <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-[15px] font-semibold text-stone-900">{cur.label}</span>
              {cur.instance && cur.instance_total > 0 && (
                <span className="font-mono-panel text-[11px] text-stone-500">
                  instance {cur.instance} of {cur.instance_total}
                </span>
              )}
              {cur.sentence && (
                <span className="font-mono-panel text-[11px] text-stone-500">· sentence {cur.sentence}</span>
              )}
            </div>
          </div>
        )}

        {progress.grew_note && !complete && (
          <div
            className="mt-3 flex items-start gap-1.5 rounded-sm bg-amber-50 border border-amber-200 px-3 py-2 text-[12.5px] leading-snug text-amber-900"
            data-testid="sc-progress-grew-note"
          >
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
            <span>{progress.grew_note}</span>
          </div>
        )}

        {/* Problem-type list with per-type instance progress: ✓ done · ▶ current · ○ upcoming */}
        <ul className="mt-3 space-y-2" data-testid="sc-plan-list">
          {planGroups.map((g) => {
            const st = complete ? "done" : g.status;
            const done = complete ? g.total : g.done;
            return (
              <li
                key={g.domain}
                data-testid={`sc-plan-group-${st}`}
                className={
                  "flex items-center gap-2.5 text-[14px] " +
                  (st === "done"
                    ? "text-stone-500"
                    : st === "current"
                    ? "text-stone-900 font-semibold"
                    : "text-stone-600")
                }
              >
                {st === "done" ? (
                  <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                ) : st === "current" ? (
                  <span className="text-[#8C3A2A] text-[15px] leading-none shrink-0" aria-hidden>▶</span>
                ) : (
                  <span className="text-stone-300 text-[15px] leading-none shrink-0" aria-hidden>○</span>
                )}
                <span className="flex-1">{g.label}</span>
                <span className="font-mono-panel text-[11px] text-stone-400 shrink-0">
                  {done} of {g.total} complete
                </span>
              </li>
            );
          })}
        </ul>

        {/* DEFERRED — seen during triage but intentionally postponed. Never hidden. */}
        {!complete && deferred.length > 0 && (
          <div className="mt-4 border-t border-stone-100 pt-3" data-testid="sc-deferred">
            <div className="font-mono-panel text-[9.5px] uppercase tracking-[0.18em] text-stone-400">
              Deferred for now
            </div>
            <ul className="mt-1.5 space-y-1">
              {deferred.map((d) => (
                <li key={d.domain} className="flex items-center gap-2 text-[13px] text-stone-500" data-testid="sc-deferred-item">
                  <span className="text-stone-300 leading-none" aria-hidden>•</span>
                  <span>{d.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {complete && (
          <div className="mt-3 flex items-center gap-2 border-t border-stone-100 pt-3 text-emerald-700" data-testid="sc-progress-complete">
            <Sparkles className="h-4 w-4 shrink-0" />
            <span className="text-[13.5px] font-semibold">Sentence Craft complete — nice work.</span>
          </div>
        )}
      </div>
    );
  }

  // FALLBACK: flat per-sentence plan (older payloads without grouped plan).
  const planSteps = progress.plan_steps || [];
  if (planSteps.length > 0) {
    const total = planSteps.length;
    const doneCount = complete ? total : (progress.plan_done ?? planSteps.filter((s) => s.status === "done").length);
    return (
      <div
        data-testid="sc-progress"
        className="rounded-md border-2 border-stone-200 border-l-[4px] border-l-[#8C3A2A] bg-white shadow-sm px-5 py-4"
      >
        <ScMeter progress={progress} complete={complete} />
        <div className="flex items-baseline justify-between">
          <span className="font-mono-panel text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8C3A2A]">
            Sentence Craft · what we're working on
          </span>
          <span className="font-mono-panel text-[12px] text-stone-600" data-testid="sc-progress-summary">
            {complete ? (
              <span className="text-emerald-700 font-semibold">All {total} done</span>
            ) : (
              <>
                <span className="text-stone-900 font-semibold">{doneCount}</span>
                <span className="text-stone-400"> of {total} {total === 1 ? "step" : "steps"}</span>
              </>
            )}
          </span>
        </div>

        {/* Segmented bar — one segment per planned step */}
        <div className="mt-3 flex items-center gap-1.5" data-testid="sc-progress-bar" aria-hidden="true">
          {planSteps.map((s, i) => (
            <div
              key={i}
              className={
                "h-2 flex-1 rounded-full transition-colors " +
                (complete || s.status === "done"
                  ? "bg-emerald-500"
                  : s.status === "current"
                  ? "bg-[#8C3A2A] animate-pulse"
                  : "bg-stone-200")
              }
            />
          ))}
        </div>

        {progress.grew_note && !complete && (
          <div
            className="mt-3 flex items-start gap-1.5 rounded-sm bg-amber-50 border border-amber-200 px-3 py-2 text-[12.5px] leading-snug text-amber-900"
            data-testid="sc-progress-grew-note"
          >
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
            <span>{progress.grew_note}</span>
          </div>
        )}

        {/* The full, stable step list: ✓ done · ◉ current · ○ upcoming */}
        <ul className="mt-3 space-y-1.5" data-testid="sc-progress-list">
          {planSteps.map((s, i) => {
            const st = complete ? "done" : s.status;
            return (
              <li
                key={`${s.domain}-${s.sentence}-${i}`}
                data-testid={`sc-progress-step-${st}`}
                className={
                  "flex items-start gap-2 text-[13.5px] " +
                  (st === "done"
                    ? "text-stone-500"
                    : st === "current"
                    ? "text-stone-900 font-semibold"
                    : "text-stone-600")
                }
              >
                {st === "done" ? (
                  <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                ) : st === "current" ? (
                  <Target className="h-4 w-4 text-[#8C3A2A] mt-0.5 shrink-0" />
                ) : (
                  <span className="mt-0.5 shrink-0 text-stone-300 text-[15px] leading-none" aria-hidden>○</span>
                )}
                <span className="flex-1">
                  {s.label}
                  <span className="font-mono-panel text-[10px] uppercase tracking-[0.14em] text-stone-400 ml-2">
                    sentence {s.sentence}
                  </span>
                </span>
                {st === "current" && (
                  <span className="font-mono-panel text-[9.5px] uppercase tracking-[0.16em] text-stone-400 self-center">now</span>
                )}
              </li>
            );
          })}
        </ul>

        {complete && (
          <div className="mt-3 flex items-center gap-2 border-t border-stone-100 pt-3 text-emerald-700" data-testid="sc-progress-complete">
            <Sparkles className="h-4 w-4 shrink-0" />
            <span className="text-[13.5px] font-semibold">Sentence Craft complete — nice work.</span>
          </div>
        )}
      </div>
    );
  }

  // LEGACY fallback (no plan available) — dynamic budget display.
  const completed = progress.completed || [];
  const active = progress.active || null;
  const remaining = progress.remaining_estimate ?? 0;
  const remainingList = progress.remaining || [];
  const done = progress.completed_count ?? completed.length;
  const total = progress.budget_total ?? (done + (active ? 1 : 0) + remaining);
  const grewNote = progress.grew_note || "";

  // Segment count for the visual bar: everything the budget currently holds.
  const segTotal = Math.max(total, done + (active && !complete ? 1 : 0), 1);
  const filled = complete ? segTotal : done;
  const activeSeg = complete ? 0 : active ? 1 : 0;

  return (
    <div
      data-testid="sc-progress"
      className="rounded-md border border-stone-200 border-l-[3px] border-l-[#8C3A2A] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)] px-5 py-4"
    >
      {/* Header row — prominent label + at-a-glance count */}
      <div className="flex items-baseline justify-between">
        <span className="font-mono-panel text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8C3A2A]">
          Sentence Craft · what we're working on
        </span>
        <span className="font-mono-panel text-[12px] text-stone-600" data-testid="sc-progress-summary">
          {complete ? (
            <span className="text-emerald-700 font-semibold">All done</span>
          ) : (
            <>
              <span className="text-stone-900 font-semibold">{done}</span>
              <span className="text-stone-400"> of ~{segTotal} sentence targets </span>
              {remaining > 0 ? `· about ${remaining} to go` : "· nearly done"}
            </>
          )}
        </span>
      </div>

      {/* Visual progress bar — segmented so "objects worked through" reads as milestones, not a score */}
      <div className="mt-3 flex items-center gap-1.5" data-testid="sc-progress-bar" aria-hidden="true">
        {Array.from({ length: segTotal }).map((_, i) => {
          const isDone = i < filled;
          const isActive = !isDone && i < filled + activeSeg;
          return (
            <div
              key={i}
              className={
                "h-2 flex-1 rounded-full transition-colors " +
                (isDone
                  ? "bg-emerald-500"
                  : isActive
                  ? "bg-[#8C3A2A] animate-pulse"
                  : "bg-stone-200")
              }
            />
          );
        })}
      </div>

      {/* Budget grew — transparent, one-turn acknowledgement that a new important thing appeared */}
      {!complete && grewNote && (
        <div
          className="mt-3 flex items-start gap-1.5 rounded-sm bg-amber-50 border border-amber-200 px-3 py-2 text-[12.5px] leading-snug text-amber-900"
          data-testid="sc-progress-grew-note"
        >
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
          <span>{grewNote}</span>
        </div>
      )}

      {/* Unified list of current developmental opportunities: ✓ done · ◉ current · ○ remaining.
          Dynamic — items may appear, resolve, or change as the learner revises (not a fixed checklist). */}
      {!complete && (completed.length > 0 || active || remainingList.length > 0) && (
        <ul className="mt-3 space-y-1.5" data-testid="sc-progress-list">
          {completed.map((c) => (
            <li key={`done-${c.domain}`} className="flex items-start gap-2 text-[13.5px] text-stone-500"
                data-testid="sc-progress-item-done">
              <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              <span>{c.label}</span>
            </li>
          ))}
          {active && (
            <li className="flex items-start gap-2 text-[13.5px] text-stone-900 font-semibold"
                data-testid="sc-progress-item-current">
              <Target className="h-4 w-4 text-[#8C3A2A] mt-0.5 shrink-0" />
              <span>{active.label}</span>
              <span className="font-mono-panel text-[9.5px] uppercase tracking-[0.16em] text-stone-400 font-normal self-center">now</span>
            </li>
          )}
          {remainingList.map((r, i) => (
            <li key={`rem-${r.domain}-${i}`} className="flex items-start gap-2 text-[13.5px] text-stone-600"
                data-testid="sc-progress-item-remaining">
              <span className="mt-0.5 shrink-0 text-stone-300 text-[15px] leading-none" aria-hidden>○</span>
              <span>{r.label}</span>
            </li>
          ))}
        </ul>
      )}

      {complete && completed.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="sc-progress-completed">
          {completed.map((c) => (
            <li key={c.domain} className="flex items-start gap-2 text-[13.5px] text-stone-700">
              <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              <span className="font-medium">{c.label}</span>
            </li>
          ))}
        </ul>
      )}

      {complete && (
        <div className="mt-3 flex items-center gap-2 border-t border-stone-100 pt-3 text-emerald-700" data-testid="sc-progress-complete">
          <Sparkles className="h-4 w-4 shrink-0" />
          <span className="text-[13.5px] font-semibold">Sentence Craft complete — nice work.</span>
        </div>
      )}
    </div>
  );
}
